"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var util_1 = require("@firebase/util");
var childSet_1 = require("./childSet");
var util_2 = require("@firebase/util");
var Node_1 = require("./Node");
var PriorityIndex_1 = require("./indexes/PriorityIndex");
var KeyIndex_1 = require("./indexes/KeyIndex");
var _defaultIndexMap;
var fallbackObject = {};
/**
 *
 * @param {Object.>} indexes
 * @param {Object.} indexSet
 * @constructor
 */
var IndexMap = /** @class */ (function () {
 function IndexMap(indexes_, indexSet_) {
 this.indexes_ = indexes_;
 this.indexSet_ = indexSet_;
 }
 Object.defineProperty(IndexMap, "Default", {
 /**
 * The default IndexMap for nodes without a priority
 * @type {!IndexMap}
 * @const
 */
 get: function () {
 util_1.assert(fallbackObject && PriorityIndex_1.PRIORITY_INDEX, 'ChildrenNode.ts has not been loaded');
 _defaultIndexMap =
 _defaultIndexMap ||
 new IndexMap({ '.priority': fallbackObject }, { '.priority': PriorityIndex_1.PRIORITY_INDEX });
 return _defaultIndexMap;
 },
 enumerable: true,
 configurable: true
 });
 /**
 *
 * @param {!string} indexKey
 * @return {?SortedMap.}
 */
 IndexMap.prototype.get = function (indexKey) {
 var sortedMap = util_2.safeGet(this.indexes_, indexKey);
 if (!sortedMap)
 throw new Error('No index defined for ' + indexKey);
 if (sortedMap === fallbackObject) {
 // The index exists, but it falls back to just name comparison. Return null so that the calling code uses the
 // regular child map
 return null;
 }
 else {
 return sortedMap;
 }
 };
 /**
 * @param {!Index} indexDefinition
 * @return {boolean}
 */
 IndexMap.prototype.hasIndex = function (indexDefinition) {
 return util_2.contains(this.indexSet_, indexDefinition.toString());
 };
 /**
 * @param {!Index} indexDefinition
 * @param {!SortedMap.} existingChildren
 * @return {!IndexMap}
 */
 IndexMap.prototype.addIndex = function (indexDefinition, existingChildren) {
 util_1.assert(indexDefinition !== KeyIndex_1.KEY_INDEX, "KeyIndex always exists and isn't meant to be added to the IndexMap.");
 var childList = [];
 var sawIndexedValue = false;
 var iter = existingChildren.getIterator(Node_1.NamedNode.Wrap);
 var next = iter.getNext();
 while (next) {
 sawIndexedValue =
 sawIndexedValue || indexDefinition.isDefinedOn(next.node);
 childList.push(next);
 next = iter.getNext();
 }
 var newIndex;
 if (sawIndexedValue) {
 newIndex = childSet_1.buildChildSet(childList, indexDefinition.getCompare());
 }
 else {
 newIndex = fallbackObject;
 }
 var indexName = indexDefinition.toString();
 var newIndexSet = util_2.clone(this.indexSet_);
 newIndexSet[indexName] = indexDefinition;
 var newIndexes = util_2.clone(this.indexes_);
 newIndexes[indexName] = newIndex;
 return new IndexMap(newIndexes, newIndexSet);
 };
 /**
 * Ensure that this node is properly tracked in any indexes that we're maintaining
 * @param {!NamedNode} namedNode
 * @param {!SortedMap.} existingChildren
 * @return {!IndexMap}
 */
 IndexMap.prototype.addToIndexes = function (namedNode, existingChildren) {
 var _this = this;
 var newIndexes = util_2.map(this.indexes_, function (indexedChildren, indexName) {
 var index = util_2.safeGet(_this.indexSet_, indexName);
 util_1.assert(index, 'Missing index implementation for ' + indexName);
 if (indexedChildren === fallbackObject) {
 // Check to see if we need to index everything
 if (index.isDefinedOn(namedNode.node)) {
 // We need to build this index
 var childList = [];
 var iter = existingChildren.getIterator(Node_1.NamedNode.Wrap);
 var next = iter.getNext();
 while (next) {
 if (next.name != namedNode.name) {
 childList.push(next);
 }
 next = iter.getNext();
 }
 childList.push(namedNode);
 return childSet_1.buildChildSet(childList, index.getCompare());
 }
 else {
 // No change, this remains a fallback
 return fallbackObject;
 }
 }
 else {
 var existingSnap = existingChildren.get(namedNode.name);
 var newChildren = indexedChildren;
 if (existingSnap) {
 newChildren = newChildren.remove(new Node_1.NamedNode(namedNode.name, existingSnap));
 }
 return newChildren.insert(namedNode, namedNode.node);
 }
 });
 return new IndexMap(newIndexes, this.indexSet_);
 };
 /**
 * Create a new IndexMap instance with the given value removed
 * @param {!NamedNode} namedNode
 * @param {!SortedMap.} existingChildren
 * @return {!IndexMap}
 */
 IndexMap.prototype.removeFromIndexes = function (namedNode, existingChildren) {
 var newIndexes = util_2.map(this.indexes_, function (indexedChildren) {
 if (indexedChildren === fallbackObject) {
 // This is the fallback. Just return it, nothing to do in this case
 return indexedChildren;
 }
 else {
 var existingSnap = existingChildren.get(namedNode.name);
 if (existingSnap) {
 return indexedChildren.remove(new Node_1.NamedNode(namedNode.name, existingSnap));
 }
 else {
 // No record of this child
 return indexedChildren;
 }
 }
 });
 return new IndexMap(newIndexes, this.indexSet_);
 };
 return IndexMap;
}());
exports.IndexMap = IndexMap;

//# sourceMappingURL=IndexMap.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/snap/IndexMap.js
// module id = 39
// module chunks = 0